import { useEffect, useId, useRef, useState } from "react";
import { useLocation, useSearch } from "wouter";
import { Search, Car, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ModalShell } from "@/components/ui/modal-shell";
import { useAddressAutocomplete } from "@/hooks/useAddressAutocomplete";
import { useMakes } from "@/hooks/useCars";
import { catalogueSearchChoice, headerSearchDestination, type HeaderSearchChoice } from "@/lib/header-search";
import { cn } from "@/lib/utils";

/** Explicit car/place choices avoid interpreting e.g. Lincoln as the wrong intent. */
export function HeaderSearch({ className }: { className?: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revision = useRef(0);
  const submitting = useRef<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [location, navigate] = useLocation();
  const currentSearch = useSearch();
  const addresses = useAddressAutocomplete();
  const { data: inventory, isPending: inventoryLoading, isError: inventoryError, refetch } = useMakes(open);
  const id = useId();
  const label = t("discovery.search_label", "Search cars or places");
  const choices: HeaderSearchChoice[] = text.trim() ? [
    ...(!inventoryLoading && !inventoryError ? [catalogueSearchChoice(text, inventory?.makes ?? [])] : []),
    ...addresses.suggestions.map(place => ({ kind: "place" as const, place })),
  ] : [];

  useEffect(() => { setActive(0); }, [text]);
  useEffect(() => () => { revision.current++; }, []);
  const invalidate = () => {
    revision.current++;
    submitting.current = null;
    setResolving(false);
    setError(null);
  };
  const choose = (choice: HeaderSearchChoice) => {
    invalidate();
    navigate(headerSearchDestination(location === "/search" ? currentSearch : "", choice));
    setOpen(false);
    setText("");
    addresses.clear();
  };
  const close = () => { invalidate(); setOpen(false); addresses.clear(); setText(""); };
  const submit = async () => {
    if (submitting.current === revision.current) return;
    if (!text.trim()) { setError(t("discovery.enter_search", "Enter a car or place to search.")); return; }
    if (choices[active]) { choose(choices[active]); return; }
    const id = revision.current;
    submitting.current = id;
    setResolving(true);
    setError(null);
    try {
      const places = await addresses.resolve(text);
      if (revision.current !== id) return;
      if (places[0]) choose({ kind: "place", place: places[0] });
      else setError(t("discovery.place_lookup_empty", "No matching place found. Check the name and try again, or choose cars when available."));
    } catch {
      if (revision.current === id) setError(t("discovery.place_lookup_error", "Place lookup failed or took too long. Press Search to try again."));
    } finally {
      if (revision.current === id) { submitting.current = null; setResolving(false); }
    }
  };

  return <>
    <button ref={triggerRef} type="button" onClick={() => setOpen(true)} aria-label={label} title={label}
      data-testid="header-search-trigger"
      className={cn("flex h-11 w-11 shrink-0 items-center justify-center rounded-full hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-current", className)}>
      <Search className="h-5 w-5" strokeWidth={1.75} />
    </button>
    <ModalShell open={open} onClose={close} title={label} size="lg" data-testid="header-search-modal"
      mobileVisualViewport padBody={false} bodyClassName="flex flex-col overflow-hidden"
      onCloseAutoFocus={event => {
        event.preventDefault();
        triggerRef.current?.focus({ preventScroll: true });
      }}
      headerExtra={<div className="px-5 py-3 sm:px-6">
        <label htmlFor={`${id}-input`} className="sr-only sm:not-sr-only sm:mb-2 sm:block sm:text-sm sm:font-medium pointer-coarse:sr-only pointer-coarse:mb-0">
          {t("discovery.search_hint", "Enter a make, model, city or country")}
        </label>
        <input id={`${id}-input`} form={`${id}-form`} {...{toolparamdescription:"City or place to search for public classic-car listings."}}
          autoFocus type="search" value={text}
          placeholder={t("discovery.search_hint", "Enter a make, model, city or country")}
          role="combobox" aria-autocomplete="list" aria-expanded={choices.length > 0}
          aria-controls={`${id}-options`} aria-activedescendant={choices[active] ? `${id}-option-${active}` : undefined}
           onChange={event => { invalidate(); setActive(0); setText(event.target.value); addresses.search(event.target.value); }}
          onKeyDown={event => {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
              event.preventDefault();
              setActive(index => choices.length ? (index + (event.key === "ArrowDown" ? 1 : -1) + choices.length) % choices.length : 0);
            }
          }}
          className="w-full rounded-xl border border-input bg-background px-4 py-3 text-base text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
        />
      </div>}
      footer={<button type="submit" form={`${id}-form`} disabled={resolving} aria-busy={resolving}
        className="primary-action min-h-11 w-full rounded-full px-4 py-2 font-semibold disabled:opacity-50">
        {t("common.search", "Search")}
      </button>}>
      <form id={`${id}-form`} {...{toolname:`search_cars_form_${id.replace(/[^a-z0-9]/gi,"_")}`,tooldescription:"Fill public car search fields for human review; no automatic submission, booking or payment."}}
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={event => { event.preventDefault(); void submit(); }}>
        <div data-testid="header-search-suggestions" className="min-h-0 overflow-y-auto overscroll-contain px-5 py-3 sm:px-6 sm:py-6 [touch-action:pan-y]">
        <p className="text-sm text-muted-foreground">
          {t("discovery.search_instruction", "Choose cars or a place below. Your existing dates and filters are kept.")}
        </p>
        <ul id={`${id}-options`} role="listbox" aria-label={label} className="mt-3">
          {choices.map((choice, index) => <li key={choice.kind === "car" ? "car" : `${choice.place.displayName}-${index}`}
            role="option" id={`${id}-option-${index}`} aria-selected={active === index}>
            <button type="button" tabIndex={-1} onMouseEnter={() => setActive(index)} onClick={() => choose(choice)}
              className={cn("flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-foreground", active === index ? "bg-muted" : "hover:bg-muted")}>
              {choice.kind === "car" ? <Car className="h-5 w-5 shrink-0" /> : <MapPin className="h-5 w-5 shrink-0" />}
              {choice.kind === "car"
                ? t("discovery.cars_matching", { defaultValue: "Cars matching “{{query}}”", query: text.trim() })
                : choice.place.displayName}
            </button>
          </li>)}
        </ul>
        {inventoryLoading && <p role="status" className="mt-2 text-sm text-muted-foreground">{t("discovery.loading_cars", "Loading car makes…")}</p>}
        {inventoryError && <div role="alert" className="mt-2 text-sm text-muted-foreground">
          {t("discovery.car_search_error", "Car search is unavailable. Please try again.")}
          <button type="button" onClick={() => void refetch()} className="ml-2 underline">{t("common.retry", "Retry")}</button>
        </div>}
         {(addresses.searching || resolving) && <p role="status" aria-live="polite" className="mt-2 text-sm text-muted-foreground">{t("discovery.loading_places", "Looking for places…")}</p>}
        {text.trim().length >= 3 && !addresses.searching && !addresses.suggestions.length &&
          <p role="status" className="mt-2 text-sm text-muted-foreground">{t("discovery.no_places", "No place suggestions available. You can still search cars above.")}</p>}
         {error && <p role="alert">{error}</p>}
        </div>
      </form>
    </ModalShell>
  </>;
}
